import type { PDFDocumentProxy, PDFWorker } from 'pdfjs-dist';

import { repairContradictedImages } from './pdf-repair';

/**
 * pdf.js, loaded the way app-web's `UiPdfService` loads it: the legacy build
 * (the modern one needs APIs Safari 18 lacks), one shared worker, and the
 * document read as bytes so a mis-described image can be repaired first.
 */

type PdfJs = typeof import('pdfjs-dist/legacy/build/pdf.mjs');

/** Where scripts/copy-pdfjs-assets.js puts the cmaps, standard fonts and wasm decoders. */
const ASSETS_PATH = '/pdfjs/';

let library: Promise<PdfJs> | undefined;
let sharedWorker: Promise<PDFWorker> | undefined;

export function loadLibrary(): Promise<PdfJs> {
  library ??= Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
  ]).then(([pdfjs, worker]) => {
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    return pdfjs;
  });
  return library;
}

function loadWorker(): Promise<PDFWorker> {
  sharedWorker ??= loadLibrary().then(async (pdfjs) => {
    const worker = new pdfjs.PDFWorker();
    await worker.promise;
    return worker;
  });
  const pending = sharedWorker;
  return pending.then(
    (worker) => {
      if (!worker.destroyed) {
        return worker;
      }
      if (sharedWorker === pending) {
        sharedWorker = undefined;
      }
      return loadWorker();
    },
    (error) => {
      if (sharedWorker === pending) {
        sharedWorker = undefined;
      }
      throw error;
    },
  );
}

/** Fetch a PDF and open it. Aborting destroys the loading task. */
export async function loadPdf(url: string, signal?: AbortSignal): Promise<PDFDocumentProxy> {
  const [pdfjs, worker, bytes] = await Promise.all([
    loadLibrary(),
    loadWorker(),
    fetchBytes(url, signal),
  ]);
  const task = pdfjs.getDocument({
    data: bytes,
    worker,
    cMapUrl: `${ASSETS_PATH}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${ASSETS_PATH}standard_fonts/`,
    // Scanner formats (JBIG2, CCITTFax, JPEG2000) decode in wasm; without it a scan draws blank.
    wasmUrl: `${ASSETS_PATH}wasm/`,
  });
  signal?.addEventListener('abort', () => void task.destroy());
  return task.promise;
}

async function fetchBytes(url: string, signal?: AbortSignal): Promise<Uint8Array> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Could not fetch the document: ${response.status} ${response.statusText}`);
  }
  const bytes = new Uint8Array(await response.arrayBuffer());
  repairContradictedImages(bytes);
  return bytes;
}

// Safari and iOS hand back a blank canvas past roughly this many pixels.
const MAX_CANVAS_PIXELS = 16_000_000;

/** The scale that fills `width`, sharpened for the screen and held under the canvas ceiling. */
export function pageRenderScale(
  width: number,
  pageWidth: number,
  pageHeight: number,
  pixelRatio: number,
): number {
  const scale = (width / pageWidth) * pixelRatio;
  const pixels = pageWidth * scale * (pageHeight * scale);
  return pixels > MAX_CANVAS_PIXELS ? scale * Math.sqrt(MAX_CANVAS_PIXELS / pixels) : scale;
}
